<template>
  <div>
    <page-title value="Certification Settings"></page-title>

    <v-card variant="flat" class="mt-6 pa-2" color="grey-lighten-5">
      <v-container>
        <v-row>
          <v-col cols="3">
            <h5>Class</h5>
            <class-picker
              :classes="classes"
              v-model="searchFilter.classId"
            ></class-picker>
          </v-col>

          <v-col cols="3">
            <h5>Age</h5>
            <age-picker v-model="searchFilter.age"></age-picker>
          </v-col>

          <v-col cols="3">
            <h5>Gender</h5>
            <v-select
              v-model="searchFilter.gender"
              :items="genderItems"
              variant="outlined"
              density="compact"
            ></v-select>
          </v-col>

          <v-col cols="3" align-self="center">
            <v-checkbox
              v-model="searchFilter.isQuit"
              color="primary"
              label="Withdrawn"
            ></v-checkbox>
          </v-col>
        </v-row>
        <v-row justify="center" no-gutters>
          <v-col cols="2">
            <v-btn
              @click="searchFee"
              color="primary"
              variant="flat"
              block
              class="search-button"
              :loading="isLoading"
              >{{ $t('common.search') }}</v-btn
            >
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <div class="mt-8">
      <v-expansion-panels>
        <v-expansion-panel v-for="(item, i) in feeList" :key="i">
          <v-expansion-panel-title>
            <template v-slot:default>
              <v-row>
                <v-col cols="auto">
                  {{ item.child.lastName + ' ' + item.child.firstName }}
                </v-col>
              </v-row>
            </template>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Childcare Category</th>
                    <th>Rank</th>
                    <th>Required Childcare</th>
                    <th>Nursery Fee</th>
                    <th>-</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(feeItem, i) in item.fees" :key="i">
                    <td>
                      {{
                        certifiedCategoryItems.find(
                          (v) => v.value == feeItem.certifiedCategory
                        )?.title
                      }}
                    </td>
                    <td>{{ feeItem.rank }}</td>
                    <td>
                      {{
                        childcareCategoryItems.find(
                          (v) => v.value == feeItem.childcareCategory
                        )?.title
                      }}
                    </td>
                    <td>{{ feeItem.fee + " yen" }}</td>
                    <td>
                      <v-btn
                        @click="
                          selectChild({ child: item.child, fee: feeItem })
                        "
                        :disabled="isLoading"
                        color="primary"
                        variant="outlined"
                        small
                        >{{ $t('common.edit') }}</v-btn
                      >
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-table>
            <div class="pa-4">
              <v-btn
                @click="selectChild({ child: item.child })"
                :disabled="isLoading"
                color="primary"
                small
                variant="outlined"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <!-- dialog -->
    <v-form v-model="isDialogFormValid">
      <v-dialog v-model="editDialog" max-width="620px" persistent>
        <v-card>
          <v-card-title>Certification Settings</v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <h5>Certification Number</h5>
                  <v-text-field
                    v-model="input.certificationNumber"
                    :disabled="isUpdate"
                    variant="outlined"
                    hide-details
                    density="compact"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <h5>Certification Period Start*</h5>
                  <v-text-field
                    v-model="input.periodStart"
                    :rules="[required]"
                    clearable
                    variant="outlined"
                    hide-details
                    density="compact"
                    type="date"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <h5>Certification Period End</h5>
                  <v-text-field
                    v-model="input.periodEnd"
                    variant="outlined"
                    clearable
                    hide-details
                    density="compact"
                    type="date"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <h5>Rank</h5>
                  <v-text-field
                    v-model="input.rank"
                    :disabled="isUpdate"
                    variant="outlined"
                    hide-details
                    density="compact"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <h5>Side Dish Fee Full Exemption</h5>
                  <v-checkbox
                    v-model="input.isFoodCostExempted"
                    :disabled="isUpdate"
                    label="Full Exemption"
                  ></v-checkbox>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <h5>Childcare Certification*</h5>
                  <v-select
                    v-model="input.certifiedCategory"
                    :disabled="isUpdate"
                    :rules="[required]"
                    :items="certifiedCategoryItems"
                    variant="outlined"
                    hide-details
                    density="compact"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <h5>Required Childcare*</h5>
                  <v-select
                    v-model="input.childcareCategory"
                    :disabled="isUpdate"
                    :rules="[required]"
                    :items="childcareCategoryItems"
                    variant="outlined"
                    hide-details
                    density="compact"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <h5>Nursery Fee (Base)*</h5>
                  <v-text-field
                    v-model="input.fee"
                    :disabled="isUpdate"
                    :rules="[required]"
                    variant="outlined"
                    hide-details
                    density="compact"
                    suffix="yen"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-container>
              <v-row>
                <v-btn @click="editDialog = false" color="grey" text
                  >{{ $t('common.cancel') }}</v-btn
                >
                <v-spacer />
                <v-btn
                  @click="clickSave"
                  :disabled="isLoading || !isDialogFormValid"
                  color="primary"
                  variant="flat"
                >
                  <div v-if="isUpdate">{{ $t('common.save') }}</div>
                  <div v-else>{{ $t('common.create') }}</div>
                </v-btn>
              </v-row>
            </v-container>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-form>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import PageTitle from "@/components/atoms/PageTitle.vue";
import {
  ChildResponse,
  NurseryClassResponse,
  NurseryFeeResponse,
} from "chaild-api/lib";
import { feeModule } from "@/store/dataModules/feeModule";
import LocalDataService from "@/service/LocalDataService";
import AgePicker from "@/components/atoms/AgePicker.vue";
import ClassPicker from "@/components/atoms/ClassPicker.vue";
import { classModule } from "@/store/dataModules/classModule";
import Swal from "sweetalert2";
import ValidationMixin from "@/mixins/ValidationMixin";
import {
  invoiceModule,
  InvoiceStatus,
} from "@/store/dataModules/invoiceModule";
import dayjs from "dayjs";

export default defineComponent({
  name: "FeeSetting",

  mixins: [ValidationMixin],

  components: {
    PageTitle,
    ClassPicker,
    AgePicker,
  },

  watch: {
    editDialog(value) {
      if (!value) {
        this.clearInput();
      }
    },
  },

  data() {
    return {
      isLoading: false,
      editDialog: false,
      searchFilter: {
        gender: "" as "" | "male" | "female",
        age: null as null | number,
        classId: null as null | number,
        isQuit: false,
      },
      genderItems: [
        { title: "All", value: "" },
        { title: "Male", value: "male" },
        { title: "Female", value: "female" },
      ],
      certifiedCategoryItems: [
        { title: "Category 1", value: "one" },
        { title: "Category 2", value: "two" },
        { title: "Category 3", value: "three" },
      ],
      childcareCategoryItems: [
        { title: "Standard Hours", value: "normal" },
        { title: "Short Hours", value: "short" },
      ],
      input: {
        childId: "",
        nurseryFeeId: "",
        certificationNumber: "",
        periodStart: "",
        periodEnd: "" as string | null,
        rank: "",
        certifiedCategory: "one",
        childcareCategory: "normal",
        fee: "",
        isFoodCostExempted: false,
        prefecture: "",
        city: "",
        localCode: "",
      },
      isDialogFormValid: false,
    };
  },

  async mounted() {
    await this.searchFee();
    await this.listClass();
  },

  computed: {
    feeList: function (): NurseryFeeResponse[] {
      if (this.searchFilter.isQuit) {
        // return feeModule.feeList.filter((f) => f.child.quitDate != null);
        return feeModule.feeList.filter((f) =>
          dayjs().isAfter(f.child.quitDate)
        );
      } else {
        return feeModule.feeList.filter(
          (f) => f.child.quitDate == null || !dayjs().isAfter(f.child.quitDate)
        );
      }
    },
    classes: function (): NurseryClassResponse[] {
      return classModule.classes;
    },
    isUpdate: function (): boolean {
      return this.input.nurseryFeeId !== "";
    },
  },

  methods: {
    async searchFee() {
      this.isLoading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await feeModule.searchFee({
          nurseryId: nurseryId,
          age: this.searchFilter.age
            ? String(this.searchFilter.age)
            : undefined,
          gender: this.searchFilter.age ? this.searchFilter.gender : undefined,
          classId: this.searchFilter.classId
            ? String(this.searchFilter.classId)
            : undefined,
          limit: 1000,
          skip: 0,
        });
      }
      this.isLoading = false;
    },
    async listClass() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await classModule.listClass({
          nurseryId,
        });
      }
    },
    async clickSave() {
      if (this.isUpdate) {
        await this.updateFee();
      } else {
        await this.createFee();
      }
    },
    async createFee() {
      this.isLoading = true;
      const response = await feeModule
        .createFee({ ...this.input })
        .catch(() => {
          alert(
            "Invalid certification/fee period. Please check for overlapping periods."
          );
          this.editDialog = false;
        });
      if (response) {
        Swal.fire({ title: "Saved successfully" });
        this.editDialog = false;
        await this.searchFee();
      }
      this.isLoading = false;
    },
    async updateFee() {
      this.isLoading = true;
      const response = await feeModule
        .updateFee({ ...this.input })
        .catch(() => {
          alert(
            "Revenue for this month has been confirmed. Nursery fee/certification info for this period cannot be edited."
          );
          this.editDialog = false;
        });
      if (response) {
        Swal.fire({ title: "Saved successfully" });
        this.editDialog = false;
        await this.searchFee();
      }
      this.isLoading = false;
    },
    // selectChild(feeId?: number) {
    selectChild(input: { child: ChildResponse; fee?: NurseryFeeResponse }) {
      if (input.child) {
        // const _fee = this.feeList.find(f => f.nurseryFeeId == feeId);
        this.input.childId = String(input.child.childId);
        if (input.fee) {
          this.input = {
            childId: String(input.child.childId),
            nurseryFeeId: String(input.fee.nurseryFeeId),
            certificationNumber: input.fee.certificationNumber || "",
            periodStart: input.fee.periodStart,
            periodEnd: input.fee.periodEnd || "",
            rank: input.fee.rank || "",
            certifiedCategory: input.fee.certifiedCategory,
            childcareCategory: input.fee.childcareCategory || "normal",
            fee: String(input.fee.fee) || "",
            isFoodCostExempted: input.fee.isFoodCostExempted,
            prefecture: input.fee.prefecture || "",
            city: input.fee.city || "",
            localCode: input.fee.localCode || "",
          };
        }
      }
      this.editDialog = true;
    },
    clearInput() {
      this.input = {
        childId: "",
        nurseryFeeId: "",
        certificationNumber: "",
        periodStart: "",
        periodEnd: "" as string | null,
        rank: "",
        certifiedCategory: "one",
        childcareCategory: "normal",
        fee: "",
        isFoodCostExempted: false,
        prefecture: "",
        city: "",
        localCode: "",
      };
    },
  },
});
</script>
